import deleteIcon from "@assets/SVG/delete.svg";
import { TableDrawer } from "@components/TableDrawer/TableDrawer";
import { useQuery } from "@hooks/useQuery";
import { useDocumentDetails } from "@modules/ECMS/MAGP/ApprovalManagement/Drilldown.Hooks";
import { Button, Modal, Pagination, TableColumnsType, Tabs, Typography } from "antd";
import { debounce } from "lodash";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ContentWrapper, DataTable } from "~/Components";
import { useNotification } from "~/Hooks/useNotification";
import ActionDropdown from "~/Modules/ECMS/MAGP/StatusInformation/ActionDropdown";
import { get, remove } from "~/Services";
import { drilldownData } from "~/Utils/drilldownData";
import { drilldownDataCorporate } from "~/Utils/drilldownDataDum";
import { getCurrentModule } from "~/Utils/StringConstants";
import {
  passengerAirportColumn,
  renderActionsColumn,
  renderDateColumn,
  renderUpdatedAtColumn,
} from "~/Utils/TableRenderFunction";
import { addSasToImages, appendSasToken } from "./Imagehelper";

export function formatSubModule(value: string | null | undefined): string {
  if (!value) {
    return "";
  }
  return value.includes("_")
    ? value
        .split("_")
        .map((segment) => segment.charAt(0).toUpperCase() + segment.slice(1))
        .join("/")
    : value.charAt(0).toUpperCase() + value.slice(1);
}
export function unformatSubModule(value: string | null | undefined): string {
  if (!value) {
    return "";
  }
  return value
    .split("/")
    .map((segment) => segment.charAt(0).toLowerCase() + segment.slice(1))
    .join("_")
    .replace(/_$/, "");
}
// Function to dynamically generate fields from payload
export const generateFieldsFromPayload = (payload: any, prefix: string = ""): any[] => {
  if (!payload || typeof payload !== "object") return [];

  const fields: any[] = [];

  // Define language prefixes to detect multilingual structures
  const languagePrefixes = ["field_content_english", "field_content_malay", "field_content_mandarin"];
  const languageLabels = { english: "EN", malay: "MS", mandarin: "ZH" };

  Object.keys(payload).forEach((key) => {
    if (key.toLowerCase() === "id") return;
    const value = payload[key];
    const baseLabel = key.charAt(0).toUpperCase() + key.slice(1).replace(/_/g, " ");
    const valuePath = prefix ? `${prefix}.${key}` : key;

    // Check if the key matches a language prefix
    const isLanguageField = languagePrefixes.includes(key);
    const languageCode = isLanguageField ? key.split("_").pop() : null;
    const langLabel = languageCode ? ` (${languageLabels[languageCode as keyof typeof languageLabels]})` : "";

    if (typeof value === "string") {
      let fieldType = "text"; // Default to text
      let formType = "input";
      let validation = { required: `${baseLabel}${langLabel} is required` };

      if (value.match(/\.(jpg|jpeg|png|gif)$/i)) {
        fieldType = "image";
        formType = "file";
        validation = { required: `${baseLabel}${langLabel} is required` };
      } else if (value.match(/\.(pdf)$/i)) {
        fieldType = "pdf";
        formType = "file";
      } else if (value.match(/^https?:\/\//)) {
        fieldType = "link"; // Assuming TableDrawer can handle links as text
        formType = "input";
      }

      fields.push({
        fieldName: `${baseLabel}${langLabel}`,
        fieldValue: valuePath, // Full path for direct access
        ...(prefix ? { parseField: prefix } : {}), // Add parseField only if nested
        className: "col-span-6",
        type: fieldType,
        name: valuePath, // Full path for reference
        formType,
        label: `${baseLabel}${langLabel}`,
        placeholder:
          fieldType === "image" || fieldType === "pdf"
            ? `Select ${baseLabel}${langLabel}`
            : `Enter ${baseLabel}${langLabel}`,
        validation,
      });
    } else if (typeof value === "number") {
      fields.push({
        fieldName: `${baseLabel}${langLabel}`,
        fieldValue: valuePath,
        ...(prefix ? { parseField: prefix } : {}),
        className: "col-span-6",
        type: "number",
        name: valuePath,
        formType: "input",
        label: `${baseLabel}${langLabel}`,
        placeholder: `Enter ${baseLabel}${langLabel}`,
        validation: { required: `${baseLabel}${langLabel} is required` },
      });
    } else if (typeof value === "boolean") {
      fields.push({
        fieldName: `${baseLabel}${langLabel}`,
        fieldValue: valuePath,
        ...(prefix ? { parseField: prefix } : {}),
        className: "col-span-6",
        type: "checkbox",
        name: valuePath,
        formType: "checkbox",
        label: `${baseLabel}${langLabel}`,
        placeholder: "",
      });
    } else if (Array.isArray(value)) {
      if (value.length > 0 && typeof value[0] === "object") {
        const subFields = generateFieldsFromPayload(value[0], valuePath);
        fields.push({
          fieldName: `${baseLabel}${langLabel}`,
          fieldValue: valuePath,
          ...(prefix ? { parseField: prefix } : {}),
          className: "col-span-12",
          type: "dynamicRow",
          name: valuePath,
          formType: "dynamicRow",
          dynamicFormType: "dynamicRow",
          label: `${baseLabel}${langLabel}`,
          placeholder: "",
          rowConfig: [
            {
              fields: subFields.map((subField) => ({
                ...subField,
                fieldValue: subField.fieldValue.replace(`${valuePath}.`, ""), // Relative path for subfields
                name: subField.fieldValue, // Keep full path for reference
              })),
            },
          ],
        });
      } else {
        fields.push({
          fieldName: `${baseLabel}${langLabel}`,
          fieldValue: valuePath,
          ...(prefix ? { parseField: prefix } : {}),
          className: "col-span-6",
          type: "text", // Treat flat arrays as text for simplicity
          name: valuePath,
          formType: "input",
          label: `${baseLabel}${langLabel}`,
          placeholder: `Enter ${baseLabel}${langLabel} items`,
        });
      }
    } else if (typeof value === "object" && value !== null) {
      if (isLanguageField && value.data) {
        // Handle language-specific nested objects (e.g., field_content_english.data)
        const nestedFields = generateFieldsFromPayload(value.data, `${key}.data`);
        nestedFields.forEach((field) => {
          fields.push({
            ...field,
            fieldName: `${field.fieldName}${langLabel}`,
            fieldValue: field.fieldValue.replace(`${key}.`, ""), // Relative path under parseField
            parseField: key, // Set parseField to the language prefix
            label: `${field.label}${langLabel}`,
            placeholder: `${field.placeholder}${langLabel}`,
            ...(field.validation ? { validation: { required: `${field.label}${langLabel} is required` } } : {}),
          });
        });
      } else {
        // Generic nested object
        const nestedFields = generateFieldsFromPayload(value, valuePath);
        fields.push(...nestedFields);
      }
    }
  });

  return fields;
};

export const CentralizedStatusInformation = () => {
  const navigate = useNavigate();
  const query = useQuery();
  const currentModule = getCurrentModule();

  const [drawerVisible, setDrawerVisible] = useState(false);
  const [selectedRowData, setSelectedRowData] = useState<any>(null);

  const [isLoading, setIsLoading] = useState(false);
  const [data, setData] = useState<any>([]);
  const [approvalId, setApprovalId] = useState<string | undefined>(selectedRowData?.approvalId);

  const [searchString, setSearchString] = useState("");
  const [currentPage, setCurrentPage] = useState<number>(0);
  const [recordsPerPage, setRecordsPerPage] = useState<number>(10);
  const [totalRecords, setTotalRecords] = useState<number>(0);
  const [sortOrder, setSortOrder] = useState<string>("desc");
  const [selectedId, setSelectedId] = useState<string>("");
  const [deleteModalVisible, setDeleteModalVisible] = useState<boolean>(false);
  const [isDeleting, setIsDeleting] = useState<boolean>(false);
  const [orderByName, setOrderByName] = useState<string>("createdAt");
  const module = getCurrentModule();
  const [corporateDrilldown, setCorporateDrilldown] = useState<any>();
  const [fetchCorporateDrilldown, setFetchCorporateDrilldown] = useState<any>();
  const [corporateIndex, setCorporateIndex] = useState<number>();

  const { openToast } = useNotification();

  console.log("fetchCorporateDrilldown", corporateDrilldown, fetchCorporateDrilldown, corporateIndex);
  const fetchDrilldownData = async () => {
    try {
      const drilldownDataForCorporate: any = await drilldownDataCorporate();
      console.log("drilldownDataForCorporate", drilldownDataForCorporate);
      setCorporateDrilldown(drilldownDataForCorporate);
    } catch (error) {
      console.error("Error fetching drilldown data:", error);
    }
  };
  useEffect(() => {
    fetchDrilldownData();
  }, []);

  useEffect(() => {
    if (selectedRowData?.module === "CorporateProfile") {
      const [module, subModule, thirdChildSubModule, index] = selectedRowData?.subModule?.split("_") || [];
      if (module === "ourPolicies" || module === "ourAirports") {
        if (subModule === "ourAirportsPageHeading" || subModule === "ourPoliciesPageHeading") {
          setFetchCorporateDrilldown(subModule);
          return;
        }
        if ([module, subModule, thirdChildSubModule, index].includes("nextPage")) {
          setFetchCorporateDrilldown(`${subModule}OurPolicies`);
          return;
        }
        setCorporateIndex(index);
        setFetchCorporateDrilldown(subModule + module.charAt(0).toUpperCase() + module.slice(1));
      } else if (module === "ourProfile") {
        if (subModule === "ourProfilePageHeading") {
          setFetchCorporateDrilldown(subModule);
          return;
        }
        if ([module, subModule, thirdChildSubModule, index].includes("nextPage")) {
          setFetchCorporateDrilldown(`${subModule}OurPolicies`);
          return;
        }
        setFetchCorporateDrilldown(module);
        setCorporateIndex(thirdChildSubModule);
      } else if (module === "ourBrand") {
        setFetchCorporateDrilldown(subModule);
        return;
      }
    } else if (selectedRowData?.module === "Subsidiaries") {
      const [module, subModule, index] = selectedRowData?.subModule?.split("_") || [];
      if (module === "subsidiariesPageHeading") {
        return;
      }
      if ([module, subModule, index].includes("nextPage")) {
        setFetchCorporateDrilldown(`nextPageSubsidiaries`);
        return;
      }
      setFetchCorporateDrilldown(module);
      setCorporateIndex(index);
    } else if (selectedRowData?.module === "Sustainability") {
      const [module, subModule, thirdChildSubModule, index] = selectedRowData?.subModule?.split("_") || [];
      if (module === "landing") {
        if (subModule === "landingPageHeading") {
          setFetchCorporateDrilldown(subModule);
          return;
        }
        setFetchCorporateDrilldown(module);
        setCorporateIndex(thirdChildSubModule);
      } else if (module === "ftse4good") {
        setFetchCorporateDrilldown(subModule);
        return;
      } else if (module === "reports") {
        if (subModule === "reportsMainContent") {
          setFetchCorporateDrilldown(subModule);
          return;
        } else {
          setFetchCorporateDrilldown(subModule);
          setCorporateIndex(index);
          return;
        }
      } else {
        if (
          subModule === "practisingSensibleEconomicsPageHeading" ||
          subModule === "environmentalConsciousnessPageHeading" ||
          subModule === "creatingInspiringWorkplacePageHeading" ||
          subModule === "communityFriendlyOrganisationPageHeading" ||
          subModule === "memorableAirportExperiencesPageHeading" ||
          subModule === "ftse4GoodPageHeading" ||
          subModule === "reportsPageHeading"
        ) {
          setFetchCorporateDrilldown(subModule);
          return;
        }
        setFetchCorporateDrilldown(module + subModule);
        setCorporateIndex(index);
      }
    } else if (selectedRowData?.module === "Media Center") {
      const [module, subModule, thirdChildSubModule, index] = selectedRowData?.subModule?.split("_") || [];
      if (module === "news") {
        if (
          subModule === "newsPageHeading" ||
          subModule === "latestNewsPageHeading" ||
          subModule === "archivesNewsPageHeading" ||
          subModule === "latestNewsNextPage" ||
          subModule === "archivesNewsNextPage"
        ) {
          setFetchCorporateDrilldown(subModule);
          return;
        } else {
          setFetchCorporateDrilldown(module);
        }
      }
    } else if (selectedRowData?.module === "Awards") {
      const [module, subModule, thirdChildSubModule, index] = selectedRowData?.subModule?.split("_") || [];
      setFetchCorporateDrilldown(subModule);
    } else if (selectedRowData?.module === "Organization") {
      const [module, subModule, thirdChildSubModule, index] = selectedRowData?.subModule?.split("_") || [];
      setFetchCorporateDrilldown(subModule);
    } else if (selectedRowData?.module === "Malaysia Airport Academy") {
      const [module, subModule, thirdChildSubModule, index] = selectedRowData?.subModule?.split("_") || [];
      if (
        subModule === "latestNews" ||
        subModule === "latestNewsList" ||
        subModule === "parentHeader" ||
        subModule === "childHeader" ||
        subModule === "grandChildHeader" ||
        subModule === "milestoneLinks" ||
        subModule === "milestoneOverview"
      ) {
        setFetchCorporateDrilldown(subModule);
        return;
      }
      setFetchCorporateDrilldown(module);
    } else if (selectedRowData?.module === "Stake Holders") {
      const [module, subModule, thirdChildSubModule, index] = selectedRowData?.subModule?.split("_") || [];
      if (
        subModule === "agenciesPageHeading" ||
        subModule === "airlinesPageHeading" ||
        subModule === "cargoPageHeading" ||
        subModule === "groundHandlersPageHeading" ||
        subModule === "agenciesNextPage" ||
        subModule === "airlinesNextPage" ||
        subModule === "cargoNextPage" ||
        subModule === "groundHandlersNextPage"
      ) {
        setFetchCorporateDrilldown(subModule);
      } else {
        setFetchCorporateDrilldown(module);
      }
    } else if (selectedRowData?.module === "Navigation") {
      const [module, subModule, thirdChildSubModule, index] = selectedRowData?.subModule?.split("_") || [];
      if (thirdChildSubModule) {
        setFetchCorporateDrilldown(thirdChildSubModule);
        return;
      } else if (subModule === "aboutAndLegal") {
        setFetchCorporateDrilldown(subModule);
        return;
      }
      setFetchCorporateDrilldown(module);
    } else if (selectedRowData?.module === "Contact Us") {
      const [module, subModule, thirdChildSubModule, index] = selectedRowData?.subModule?.split("_") || [];
      if (subModule === "corporatePageContent") {
        setFetchCorporateDrilldown(thirdChildSubModule);
        return;
      }
      setFetchCorporateDrilldown(subModule);
    } else if (selectedRowData?.module === "Business Opportunities") {
      const [module, subModule, thirdChildSubModule, index] = selectedRowData?.subModule?.split("_") || [];
      if (module === "commercialLeasingOpportunitiesList") {
        return;
      }
      if (thirdChildSubModule) {
        setFetchCorporateDrilldown(module);
        setCorporateIndex(thirdChildSubModule);
        return;
      }
      setFetchCorporateDrilldown(subModule);
    }
  }, [selectedRowData]);

  // const [actionType, setActionType] = useState<string>();
  const [actionType, setActionType] = useState<Record<string, string | undefined>>({});

  const processFlowChartTabs: any[] = [
    "businessCaseProcessFlowChart",
    "strategyPaperProcessFlowChartOpenTenderInvitationToBid",
    "strategyPaperProcessFlowChartDirectAppointment",
    "annualProcurementPlanningProcessFlowChart",
    "procurementPlanningProcessFlowChart",
    "processingofPurchaseRequisitionProcessFlowChart",
    "purchaseRequisitionProcessFlowChart",
    "requisitionProcessFlowChart",
    "clarificationNegotiationpriortoAwardProcessFlowChartClarificationPriortoAward",
    "clarificationNegotiationpriortoAwardProcessFlowChartNegotiation",
    "abortofTenderProcessFlowChart",
    "tenderEvaluationProcessFlowChart",
    "tenderAdministrationProcessFlowChartbelowRM5000000",
    "tenderAdministrationProcessFlowChartaboveRM5000000",
    "tenderManagementProcessFlowChart",
    "sourcingProcessFlowChart",
    "contractCloseoutProcessFlowChart",
    "contractTerminationProcessFlowChart",
    "contractRenewalProcessFlowChart",
    "contractManagementImplementationProcessFlowChart",
    "contractAdministrationProcessFlowChart",
    "vendorPerformanceManagementProcessFlowChartSchedule",
    "vendorPerformanceManagementProcessFlowChartUnscheduled",
    "potentialVendorScreeningProcessFlowChart",
    "vendorRegistrationProcessFlowChartNewRegistration",
    "vendorRegistrationProcessFlowChartRenewal",
    "vendorManagementProcessFlowChart",
  ];

  useEffect(() => {
    setApprovalId(selectedRowData?.approvalId);
  }, [selectedRowData]);

  const {
    data: drilldown,
    error,
    isLoading: drilldownLoading,
    isError,
    refetch: refetchDocument,
  } = useDocumentDetails(approvalId || "");
  if (drilldown && typeof drilldown.payload === "string") {
    try {
      drilldown.payload = JSON.parse(drilldown.payload);
    } catch (e) {
      console.error("Error parsing JSON:", e);
      drilldown.payload = {};
    }
  }

  console.log("drilldownssss", drilldownData, drilldown, corporateDrilldown);

  // const handleDelete = (id: string) => {
  //   remove(`management/delete-document?approvalId=${id}`)
  //     .then((res) => {
  //       setDeleteModalVisible(false);
  //       setIsDeleting(false);
  //       setSelectedId("");
  //       getRecords(sortOrder, orderByName, searchString, currentPage);
  //     })
  //     .catch((err) => { });
  // };

  const handleDelete = (id: string) => {
    remove(`management/delete-document?approvalId=${id}`)
      .then((res) => {
        openToast({
          content: "Data deleted successfully",
          type: "success",
        });
        setDeleteModalVisible(false);
        setIsDeleting(false);
        setSelectedId("");
        getRecords(sortOrder, orderByName, searchString, currentPage);
      })
      .catch((err) => {
        openToast({
          content: "Failed to delete document",
          type: "error",
        });
      });
  };

  useEffect(() => {
    if (approvalId) {
      refetchDocument();
    }
  }, [approvalId]);
  const airportColumn = {
    title: "Airport",
    dataIndex: "airportName",
    key: "airportName",
    width: 100,
    sorter: true,
    sortOrder:
      sortOrder === "asc" && orderByName === "airportName"
        ? "ascend"
        : sortOrder === "desc" && orderByName === "airportName"
          ? "descend"
          : undefined,
    render: (text: string) => text || "-",
  };

  const columns: TableColumnsType = [
    {
      title: "Request Type",
      dataIndex: "action",
      key: "action",
      width: 100,
    },
    {
      title: "Airport",
      dataIndex: "airportName",
      key: "airportName",
      width: 100,
      sorter: true,
      sortOrder:
        sortOrder === "asc" && orderByName === "airportName"
          ? "ascend"
          : sortOrder === "desc" && orderByName === "airportName"
            ? "descend"
            : undefined,
      render: (text: string) => passengerAirportColumn(text),
    },
    {
      title: "Module",
      width: 100,
      dataIndex: "module",
      key: "module",
      sorter: true,
      sortOrder:
        sortOrder === "asc" && orderByName === "module"
          ? "ascend"
          : sortOrder === "desc" && orderByName === "module"
            ? "descend"
            : undefined,
    },
    {
      title: "Sub Module",
      dataIndex: "subModule",
      width: 100,
      key: "subModule",
      sorter: true,
      sortOrder:
        sortOrder === "asc" && orderByName === "subModule"
          ? "ascend"
          : sortOrder === "desc" && orderByName === "subModule"
            ? "descend"
            : undefined,
      render: (text: string) => formatSubModule(text),
    },
    {
      title: "Updated At",
      dataIndex: "createdAt",
      key: "createdAt",
      sorter: true,
      sortOrder:
        sortOrder === "asc" && orderByName === "createdAt"
          ? "ascend"
          : sortOrder === "desc" && orderByName === "createdAt"
            ? "descend"
            : undefined,
      render: renderDateColumn,
    },
    {
      title: "Updated By",
      dataIndex: "createdBy",
      width: 100,
      key: "createdBy",
      sorter: true,
      sortOrder:
        sortOrder === "asc" && orderByName === "createdBy"
          ? "ascend"
          : sortOrder === "desc" && orderByName === "createdBy"
            ? "descend"
            : undefined,
    },
    {
      title: "Approved At",
      dataIndex: "updatedAt",
      key: "updatedAt",
      sorter: true,
      sortOrder:
        sortOrder === "asc" && orderByName === "updatedAt"
          ? "ascend"
          : sortOrder === "desc" && orderByName === "updatedAt"
            ? "descend"
            : undefined,
      render: renderDateColumn,
    },
    {
      title: "Rejected At",
      dataIndex: "updatedAt",
      key: "updatedAt",
      sorter: true,
      sortOrder:
        sortOrder === "asc" && orderByName === "updatedAt"
          ? "ascend"
          : sortOrder === "desc" && orderByName === "updatedAt"
            ? "descend"
            : undefined,
      render: renderDateColumn,
    },
    {
      title: "Approved By",
      dataIndex: "updatedBy",
      width: 100,
      key: "updatedBy",
      sorter: true,
      sortOrder:
        sortOrder === "asc" && orderByName === "updatedBy"
          ? "ascend"
          : sortOrder === "desc" && orderByName === "updatedBy"
            ? "descend"
            : undefined,
      render: (text: string) => renderUpdatedAtColumn(text),
    },
    {
      title: "Rejected By",
      dataIndex: "updatedBy",
      key: "updatedBy",
      sorter: true,
      sortOrder:
        sortOrder === "asc" && orderByName === "updatedBy"
          ? "ascend"
          : sortOrder === "desc" && orderByName === "updatedBy"
            ? "descend"
            : undefined,
      render: (text: string) => renderUpdatedAtColumn(text),
    },
    {
      title: "Rejected Reason",
      dataIndex: "rejectReason",
      key: "rejectReason",
      sorter: true,
      sortOrder:
        sortOrder === "asc" && orderByName === "rejectReason"
          ? "ascend"
          : sortOrder === "desc" && orderByName === "rejectReason"
            ? "descend"
            : undefined,
    },
    {
      title: "Actions",
      key: "actions",
      width: 70,
      render: (record: any) =>
        renderActionsColumn(
          record,
          setDrawerVisible,
          setSelectedRowData,
          (e) => {
            if (record.module === "Centralized Modules") {
              e.preventDefault();
              if (record.subModule === "centralizedModules") {
                navigate(
                  `/centralized/centralizedModules/add?id=${record.approvalId}&type=${record.action.toLowerCase()}`
                );
              }
            } else if (record.module === "Centralized Banner") {
              e.preventDefault();
              navigate(
                `/centralized/${record.subModule}/add?id=${record.approvalId}&type=${record.action.toLowerCase()}`
              );
            } else if (record.module === "Centralized Airline Tail Image") {
              e.preventDefault();
              if (record.subModule === "centralizedAirlineTailImage") {
                navigate(
                  `/centralized/centralizedAirlineTailImage/add?id=${record.approvalId}&type=${record.action.toLowerCase()}`
                );
              }
            } else if (record.module === "Centralized FAQs") {
              e.preventDefault();
              navigate(
                `/centralized/centralizedFaqs/${record?.subModule}/add?id=${record.approvalId}&type=${record.action.toLowerCase()}`
              );
            } else if (record.module === "Centralized Shop & Dine") {
              e.preventDefault();

              const [activeTab, subActiveTab] = record.subModule?.split("_");

              if (activeTab === "zone") {
                navigate(
                  `/centralized/centralizedShopAndDine/${activeTab}/${subActiveTab}/add?id=${record.approvalId}&type=${record.action.toLowerCase()}`
                );
              } else if (subActiveTab === "centralizedList") {
                navigate(
                  `/centralized/centralizedShopAndDine/${subActiveTab}/add?id=${record.approvalId}&listType=${activeTab}&type=${record.action.toLowerCase()}`
                );
              } else {
                navigate(
                  `/centralized/centralizedShopAndDine/${activeTab}/add?id=${record.approvalId}&type=${record.action.toLowerCase()}`
                );
              }
            }
            // else if (record.module === "Centralized Promotions") {
            //   e.preventDefault();
            //   if (record.subModule === "centralizedPromotions") {
            //     navigate(
            //       `/centralized/centralizedPromotions/add?id=${record.approvalId}&type=${record.action.toLowerCase()}`
            //     );
            //   }
            // }
            else if (record.module?.includes("Centralized Policies")) {
              e.preventDefault();
              const actionType = record.action?.toLowerCase();
              const subModule = record.subModule;

              if (subModule === "privacyPolicyTabOne") {
                navigate(
                  `/centralized/privacyPolicy/privacyPolicyTabOne/add?id=${record.approvalId}&type=${actionType}`
                );
                return;
              }

              if (subModule === "TabOneData") {
                navigate(`/centralized/privacyPolicy/tab-1/add?id=${record.approvalId}&type=${actionType}`);
                return;
              }

              if (subModule === "myAirportsPrivacyPolicy") {
                navigate(
                  `/centralized/privacyPolicy?activeTab=myAirportsPrivacyPolicy&id=${record.approvalId}&type=${record.action.toLowerCase()}`
                );
                return;
              }
            } else if (record.module === "Traveller's Info") {
              e.preventDefault();
              const [activeTab, subActiveTab, thirdChildActiveTab, fourthChildActiveTab] = record.subModule?.split("_");
              if (
                thirdChildActiveTab === "centralizedSingleCabinLuggagePolicyPageImage" ||
                thirdChildActiveTab === "centralizedGuidelinesForCheckedinLuggagePageImage" ||
                thirdChildActiveTab === "centralizedLagsLiquidItemImage"
              ) {
                navigate(
                  `/centralized/travellersInfo/${subActiveTab}/${thirdChildActiveTab}/add?id=${record.editId || record.approvalId}`
                );
                return;
              } else if (
                thirdChildActiveTab === "centralizedRestrictedItemsCabinOnlyRestrictionsImage" ||
                thirdChildActiveTab === "centralizedRestrictedItemsCheckedInCabinRestrictionsImage"
              ) {
                navigate(
                  `/centralized/travellersInfo/${subActiveTab}/add?type=${thirdChildActiveTab}&id=${record.editId || record.approvalId}`
                );
                return;
              } else {
                navigate(
                  `/centralized/centralizedTravellersInfo?activeTab=${activeTab}&subActiveTab=${subActiveTab}&thirdChildActiveTab=${thirdChildActiveTab}&id=${record.approvalId}`
                );
                return;
              }
            } else if (record.module === "Baggage Guide") {
              e.preventDefault();
              const [activeTab, subActiveTab] = record.subModule?.split("_");
              if (
                subActiveTab === "centralizedSingleCabinLuggagePolicyPageImage" ||
                subActiveTab === "centralizedGuidelinesForCheckedinLuggagePageImage"
              ) {
                navigate(
                  `/centralized/centralizedBaggageGuide/${activeTab}/${subActiveTab}/add?id=${record.editId || record.approvalId}`
                );
                return;
              } else {
                navigate(
                  `/centralized/centralizedBaggageGuide?activeTab=${activeTab}&subActiveTab=${subActiveTab}&id=${record.approvalId}`
                );
                return;
              }
            } else if (record.module === "Centralized Promotions") {
              e.preventDefault();
              const [activeTab] = record.subModule?.split("_");
              console.log(activeTab, "promotest");
              if (
                activeTab === "notificationType" ||
                activeTab === "geoLocationOffer" ||
                activeTab === "promotion" ||
                activeTab === "repeatType" ||
                activeTab === "geoLocationPromotions" ||
                activeTab === "geoLocationPromotionLevels"
              ) {
                navigate(
                  `/centralized/centralizedPromotion/${activeTab}/add?id=${record.approvalId}&type=${record.action.toLowerCase()}`
                );
              }
            } else if (record.module === "Lags & Restricted Items") {
              e.preventDefault();
              const [activeTab, subActiveTab] = record.subModule?.split("_");
              if (
                subActiveTab === "centralizedLagsLiquidItemImage" ||
                subActiveTab === "centralizedLagsGuidelinesContent" ||
                subActiveTab === "centralizedLagsSteps"
              ) {
                navigate(
                  `/centralized/centralizedLagsAndRestrictedItems/${activeTab}/${subActiveTab}/add?id=${record.editId || record.approvalId}`
                );
                return;
              } else if (
                subActiveTab === "centralizedRestrictedItemsCabinOnlyRestrictionsImage" ||
                subActiveTab === "centralizedRestrictedItemsCheckedInCabinRestrictionsImage"
              ) {
                navigate(
                  `/centralized/centralizedLagsAndRestrictedItems/${activeTab}/add?type=${subActiveTab}&id=${record.editId || record.approvalId}`
                );
                return;
              } else {
                navigate(
                  `/centralized/centralizedLagsAndRestrictedItems?activeTab=${activeTab}&subActiveTab=${subActiveTab}&id=${record.approvalId}`
                );
                return;
              }
            } else
              navigate(
                `/centralized/${module.toLowerCase()}/${record.module.toLowerCase()}?activeTab=${record.subModule}&id=${
                  record.approvalId
                }`
              );
          },
          setSelectedId,
          setDeleteModalVisible
        ),
    },
  ];

  const handleTableSort = (column: any, sortOrder: string) => {
    let order = "";
    if (column.sorter) {
      if (sortOrder === "asc") {
        setSortOrder("desc");
        order = "desc";
      } else if (sortOrder === "desc") {
        setSortOrder("asc");
        order = "asc";
      }
    }
    setOrderByName(column.dataIndex);
    setCurrentPage(0);
    getRecords(order, column.dataIndex, searchString, currentPage);
  };

  const handleSearchChange = debounce((value: string, isLoading: boolean) => {
    const unformattedValue = unformatSubModule(value);
    setSearchString(unformattedValue);
    setIsLoading(isLoading);
  }, 1000);

  const getColumnsByTab = (activeTab: string) => {
    const isPassengerModule = module === "PASSENGER" || module === "PASSENGER ECMS";

    const baseColumnTitles: Record<string, string[]> = {
      draft: ["Request Type", "Module", "Sub Module", "Updated At", "Updated By", "Actions"],
      pending: ["Request Type", "Module", "Sub Module", "Updated At", "Updated By", "Actions"],
      approved: ["Request Type", "Module", "Sub Module", "Approved At", "Approved By", "Actions"],
      rejected: ["Request Type", "Module", "Sub Module", "Rejected At", "Rejected By", "Rejected Reason", "Actions"],
    };

    if (isPassengerModule) {
      baseColumnTitles.draft.unshift("Airport");
      baseColumnTitles.pending.unshift("Airport");
      baseColumnTitles.approved.unshift("Airport");
      baseColumnTitles.rejected.unshift("Airport");
    }

    const allowedTitles = baseColumnTitles[activeTab] || [];

    const filteredColumns = columns.filter((column) => {
      if (typeof column.title === "string") {
        return allowedTitles.includes(column.title);
      }
      return false;
    });

    return filteredColumns.map((col: any) => ({
      ...col,
      onHeaderCell: (column: any) => (col.sorter ? { onClick: () => handleTableSort(col, sortOrder) } : {}),
    }));
  };

  const items = [
    {
      label: "Draft",
      key: "draft",
      children: (
        <>
          <DataTable
            tabOffset={60}
            columns={getColumnsByTab("draft")}
            className="table-clickable"
            tableData={
              data
                ? data?.map((d: any, index: number) => {
                    return {
                      ...d,
                    };
                  })
                : []
            }
            isLoading={isLoading}
            exportFileName="CONTRACT"
            isPagination={true}
            recordsPerPage={10}
            multiSelect={false}
            hideExport={true}
            isServerSearchApi={true}
            searchPlaceholderText={"Search Module / Sub Module"}
            onSearchChange={handleSearchChange}
            // onClick={(record) => {
            //   setSelectedRowData(record);
            // }}
            onClick={(record) => {
              setSelectedRowData(record);
            }}
            additionalCTA={
              <ActionDropdown
                value={actionType["draft"]}
                onChange={(val) => {
                  setActionType((prev) => ({
                    ...prev,
                    draft: val,
                  }));
                }}
              />
            }
          />
          <div className="pagination">
            <Pagination
              defaultCurrent={1}
              current={currentPage + 1}
              total={totalRecords}
              onChange={(curPage) => {
                let page = curPage - 1;
                setCurrentPage(page);
                getRecords(sortOrder, orderByName, searchString, page);
              }}
              showSizeChanger={false}
            />
          </div>
        </>
      ),
    },
    {
      label: "Pending",
      key: "pending",
      children: (
        <>
          <DataTable
            tabOffset={60}
            columns={getColumnsByTab("pending")}
            className="table-clickable"
            tableData={
              data
                ? data?.map((d: any) => {
                    return {
                      ...d,
                    };
                  })
                : []
            }
            isLoading={isLoading}
            exportFileName="CONTRACT"
            isPagination={true}
            recordsPerPage={10}
            isServerSearchApi={true}
            multiSelect={false}
            hideExport={true}
            searchPlaceholderText={"Search Module / Sub Module"}
            onSearchChange={handleSearchChange}
            onClick={(record) => {
              setSelectedRowData(record);
            }}
            additionalCTA={
              <ActionDropdown
                value={actionType["pending"]}
                onChange={(val) => {
                  setActionType((prev) => ({
                    ...prev,
                    pending: val,
                  }));
                }}
              />
            }
          />
          <div className="pagination">
            <Pagination
              defaultCurrent={1}
              current={currentPage + 1}
              total={totalRecords}
              onChange={(curPage) => {
                let page = curPage - 1;
                setCurrentPage(page);
                getRecords(sortOrder, orderByName, searchString, page);
              }}
              showSizeChanger={false}
            />
          </div>
        </>
      ),
    },
    {
      label: "Rejected",
      key: "rejected",
      children: (
        <>
          <DataTable
            tabOffset={60}
            columns={getColumnsByTab("rejected")}
            className="table-clickable"
            tableData={
              data
                ? data?.map((d: any) => {
                    return {
                      ...d,
                    };
                  })
                : []
            }
            isLoading={isLoading}
            exportFileName="CONTRACT"
            isPagination={true}
            recordsPerPage={10}
            isServerSearchApi={true}
            multiSelect={false}
            hideExport={true}
            searchPlaceholderText={"Search Module / Sub Module"}
            onSearchChange={handleSearchChange}
            onClick={(record) => {
              setSelectedRowData(record);
              setDrawerVisible(true);
            }}
            additionalCTA={
              <ActionDropdown
                value={actionType["rejected"]}
                onChange={(val) => {
                  setActionType((prev) => ({
                    ...prev,
                    rejected: val,
                  }));
                }}
              />
            }
          />
          <div className="pagination">
            <Pagination
              defaultCurrent={1}
              current={currentPage + 1}
              total={totalRecords}
              onChange={(curPage) => {
                let page = curPage - 1;
                setCurrentPage(page);
                getRecords(sortOrder, orderByName, searchString, page);
              }}
              showSizeChanger={false}
            />
          </div>
        </>
      ),
    },
    {
      label: "Approved",
      key: "approved",
      children: (
        <>
          <DataTable
            tabOffset={60}
            columns={getColumnsByTab("approved")}
            className="table-clickable"
            tableData={
              data
                ? data?.map((d: any) => {
                    return {
                      ...d,
                    };
                  })
                : []
            }
            isLoading={isLoading}
            exportFileName="CONTRACT"
            isPagination={true}
            recordsPerPage={10}
            isServerSearchApi={true}
            multiSelect={false}
            hideExport={true}
            searchPlaceholderText={"Search Module / Sub Module"}
            onSearchChange={handleSearchChange}
            onClick={(record) => {
              setSelectedRowData(record);
              setDrawerVisible(true);
            }}
            additionalCTA={
              <ActionDropdown
                value={actionType["approved"]}
                onChange={(val) => {
                  setActionType((prev) => ({
                    ...prev,
                    approved: val,
                  }));
                }}
              />
            }
          />
          <div className="pagination">
            <Pagination
              defaultCurrent={1}
              current={currentPage + 1}
              total={totalRecords}
              onChange={(curPage) => {
                let page = curPage - 1;
                setCurrentPage(page);
                getRecords(sortOrder, orderByName, searchString, page);
              }}
              showSizeChanger={false}
            />
          </div>
        </>
      ),
    },
  ];

  const getRecords = (sortOrder: string, orderByParam: string, searchString: any, currentPage?: number) => {
    const activeTab = query?.get("activeTab") || "";
    const activeActionType = actionType[activeTab]; // undefined initially
    const searchText = encodeURIComponent(searchString);

    setIsLoading(true);

    // Only append &action if the user has selected one
    let url = `management/get-documents?page=${currentPage}&size=${recordsPerPage}&application=CENTRALIZED&status=${activeTab}&order=${sortOrder}&orderBy=${orderByParam}&searchText=${searchText}&userData=true${
      activeActionType ? `&action=${activeActionType}` : ""
    }`;

    get(url)
      .then((res: any) => {
        if (res?.response?.data) {
          setData(res.response.data.records);
          setTotalRecords(res.response.data.total);
        }
        setIsLoading(false);
      })
      .catch(() => {
        setData([]);
        setIsLoading(false);
      });
  };

  useEffect(() => {
    const activeTab = query?.get("activeTab") || "";
    getRecords("desc", "createdAt", searchString, currentPage);
  }, [query?.get("activeTab"), actionType[query?.get("activeTab") || ""]]);

  useEffect(() => {
    getRecords(sortOrder, orderByName, searchString, 0);
  }, [searchString]);

  const predefinedFields =
    drilldown?.appList?.[0]?.appName === "CORPORATE" || drilldown?.appList?.[0]?.appName === "MAHB_CORPORATE"
      ? corporateIndex !== undefined && corporateDrilldown?.[fetchCorporateDrilldown]?.[corporateIndex]
        ? corporateDrilldown?.[fetchCorporateDrilldown]?.[corporateIndex]
        : corporateDrilldown?.[fetchCorporateDrilldown] ||
          (() => {
            const subModuleParts = selectedRowData?.subModule?.split("_") || [];
            for (let i = subModuleParts.length - 1; i >= 0; i--) {
              const key = subModuleParts[i] as keyof typeof drilldownData;
              if (key in drilldownData) {
                return drilldownData[key];
              }
            }
            return undefined;
          })()
      : (() => {
          // First try exact subModule match
          const exactKey = selectedRowData?.subModule as keyof typeof drilldownData;
          if (exactKey && exactKey in drilldownData) {
            return drilldownData[exactKey];
          }
          // Fall back to last part after splitting by underscore
          const lastPart = selectedRowData?.subModule?.split("_").pop() as keyof typeof drilldownData;
          if (lastPart && lastPart in drilldownData) {
            return drilldownData[lastPart];
          }
          return undefined;
        })();

  const drawerFields = drilldown?.drillDownTab || predefinedFields || generateFieldsFromPayload(drilldown?.payload);
  // const activeTab = query?.get("activeTab") || "";
  // useEffect(() => {
  //   setActionType(undefined);
  // }, [activeTab]);
  const handleDrawerClose = () => {
    setDrawerVisible(false);
    setSelectedRowData(null);
    setApprovalId(undefined);
    setFetchCorporateDrilldown(undefined);
    setCorporateIndex(undefined);
  };

  const imageKeys = [
    "banner_image",
    "square_image",
    "image",
    "web_image",
    "tab_image",
    "video",
    "mobile_image",
    "mobile_banner_image",
    "logo",
    "pdf_url",
    "type_banner_image",
  ];

  const descriptionKeys = [
    "description",
    "description_malay",
    "description_mandarin",
    "type_description_en",
    "type_description_ms",
    "type_description_zh",
    "description_english_tab_1",
    "description_malay_tab_1",
    "description_mandarin_tab_1",
    "description_english_tab_2",
    "description_malay_tab_2",
    "description_mandarin_tab_2",
    "answer_en",
    "answer_malay",
    "answer_mandarian",
    "description_ms",
    "description_zh",
    "image_description",
    "image_description_malay",
    "image_description_mandarin",
    "description_english_1",
    "description_mandarin_1",
    "description_malay_1",
    "terms_condition",
    "terms_condition_malay",
    "terms_condition_mandarin",
    "long_description",
    "long_description_malay",
    "long_description_mandarin",
    "short_description",
    "short_description_malay",
    "short_description_mandarin",
    "description_english_2",
    "description_malay_2",
    "description_mandarin_2",
    "description_english_3",
    "description_malay_3",
    "description_mandarin_3",
    "description_english_4",
    "description_malay_4",
    "description_mandarin_4",
    "description_english_5",
    "description_malay_5",
    "description_mandarin_5",
    "body",
  ];

  const drilldownAny = drilldown?.payload as any;

  const dataWithSas = {
    ...drilldown?.payload,

    ...Object.fromEntries(imageKeys.map((key) => [key, drilldownAny?.[key] ? appendSasToken(drilldownAny[key]) : ""])),

    ...Object.fromEntries(
      descriptionKeys.map((key) => [key, drilldownAny?.[key] ? addSasToImages(drilldownAny[key]) : ""])
    ),
  };

  return (
    <ContentWrapper title={"Status Information"}>
      <Tabs
        activeKey={query?.get("activeTab") || ""}
        items={items}
        onChange={(activeKey) => {
          navigate(`${window.location.pathname}?activeTab=${activeKey}`, { replace: true });
          setSearchString("");
          setCurrentPage(0);
          setOrderByName("createdAt");
          setSortOrder("desc");
          setActionType({});
        }}
      ></Tabs>
      <TableDrawer
        title={
          <Typography.Title
            level={4}
            className={"!mb-0"}
          >
            Detailed View
          </Typography.Title>
        }
        visible={drawerVisible}
        onClose={handleDrawerClose}
        // fields={drilldownData[selectedRowData?.subModule?.split("_").pop() as keyof typeof drilldownData]}
        // fields={
        //   drilldown?.appList?.[0]?.appName === 'CORPORATE'
        //     ? (
        //         corporateIndex !== undefined && corporateDrilldown?.[fetchCorporateDrilldown]?.[corporateIndex]
        //           ? corporateDrilldown?.[fetchCorporateDrilldown]?.[corporateIndex]
        //           : corporateDrilldown?.[fetchCorporateDrilldown]
        //       ) || (() => {
        //         const subModuleParts = selectedRowData?.subModule?.split("_") || [];
        //         console.log("subModuleParts -1", subModuleParts);
        //         for (let i = subModuleParts.length - 1; i >= 0; i--) {
        //           const key = subModuleParts[i] as keyof typeof drilldownData;
        //           if (key in drilldownData) {
        //             console.log("subModuleParts -2", drilldownData[key]);
        //             return drilldownData[key];
        //           }
        //         }
        //         return undefined;
        //       })()
        //     : drilldownData[selectedRowData?.subModule?.split("_").pop() as keyof typeof drilldownData]
        // }
        fields={drawerFields}
        data={selectedRowData ? dataWithSas : undefined}
        isLoading={drilldownLoading}
        reviewNotes={
          query.get("activeTab") !== "draft" &&
          query.get("activeTab") !== "approved" &&
          query.get("activeTab") !== "rejected"
            ? drilldown?.reviewNotes
            : undefined
        }
        airportName={drilldown?.airportName}
      />
      <Modal
        open={deleteModalVisible}
        maskClosable={true}
        onCancel={() => setDeleteModalVisible(false)}
        centered
        width={420}
        footer={
          <div style={{ textAlign: "center", marginTop: -24, marginBottom: 16 }}>
            <Button
              type="default"
              onClick={() => {
                setDeleteModalVisible(false);
              }}
            >
              No, Cancel
            </Button>
            <Button
              type="primary"
              loading={isDeleting}
              onClick={() => {
                setIsDeleting(true);
                handleDelete(selectedId);
              }}
            >
              Yes, Delete
            </Button>
          </div>
        }
      >
        <div className="text-center flex flex-col items-center">
          <img
            src={deleteIcon}
            alt="Approve"
            className="h-16 mb-4"
          />
          <h2 className="text-xl font-semibold text-[#F26486] mb-4">Delete?</h2>
          <p className="text-xs text-[#51505D]">This action will Delete the Draft. Do you wish to proceed?</p>
        </div>
      </Modal>
    </ContentWrapper>
  );
};
